import { DEFAULT_SITUATION_LABELS } from "../constants/ownerPortfolio";
import {
  OwnerPortfolioData,
  PORTFOLIO_DIAGNOSES,
  PORTFOLIO_METRICS_DAYS,
  PORTFOLIO_SITUATIONS,
  PORTFOLIO_SORTS,
  PortfolioAppliedFilters,
  PortfolioDiagnosis,
  PortfolioFunnel,
  PortfolioKpis,
  PortfolioMetricsDays,
  PortfolioProperty,
  PortfolioSituation,
  PortfolioSort,
} from "../types/ownerPortfolio.types";

const EMPTY_KPIS: PortfolioKpis = {
  total: 0,
  rented: 0,
  vacant: 0,
  occupancy_rate: null,
  vacant_monthly_rent: null,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asNullableString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function asNonNegativeInteger(value: unknown, fallback: number): number {
  const parsed = asFiniteNumber(value);
  if (parsed === null || parsed < 0) {
    return fallback;
  }

  return Math.round(parsed);
}

function asSituation(value: unknown): PortfolioSituation {
  if (
    typeof value === "string" &&
    (PORTFOLIO_SITUATIONS as readonly string[]).includes(value)
  ) {
    return value as PortfolioSituation;
  }

  return "ALUGANDO";
}

function asSort(value: unknown): PortfolioSort {
  if (typeof value === "string" && (PORTFOLIO_SORTS as readonly string[]).includes(value)) {
    return value as PortfolioSort;
  }

  return "condominium";
}

function asMetricsDays(value: unknown): PortfolioMetricsDays {
  const parsed = asFiniteNumber(value);
  if (
    parsed !== null &&
    (PORTFOLIO_METRICS_DAYS as readonly number[]).includes(parsed)
  ) {
    return parsed as PortfolioMetricsDays;
  }

  return 30;
}

function asDiagnosis(value: unknown): PortfolioDiagnosis | null {
  if (
    typeof value === "string" &&
    (PORTFOLIO_DIAGNOSES as readonly string[]).includes(value)
  ) {
    return value as PortfolioDiagnosis;
  }

  return null;
}

function normalizeFunnel(value: unknown, metricsDays: PortfolioMetricsDays): PortfolioFunnel {
  const funnel = isRecord(value) ? value : {};
  const pageViews = asFiniteNumber(funnel.page_views);
  const scheduleClicks = asFiniteNumber(funnel.schedule_clicks);

  return {
    period_days: asMetricsDays(funnel.period_days ?? metricsDays),
    page_views: pageViews === null ? null : Math.round(pageViews),
    schedule_clicks: scheduleClicks === null ? null : Math.round(scheduleClicks),
    schedules: asNonNegativeInteger(funnel.schedules, 0),
    visits: asNonNegativeInteger(funnel.visits, 0),
    offers: asNonNegativeInteger(funnel.offers, 0),
    shared_listing: funnel.shared_listing === true,
    analytics_available: funnel.analytics_available === true && pageViews !== null,
    notice: asNullableString(funnel.notice),
  };
}

function normalizeProperty(
  value: unknown,
  metricsDays: PortfolioMetricsDays
): PortfolioProperty | null {
  if (!isRecord(value)) {
    return null;
  }

  const situation = asSituation(value.situation);
  const characteristicsAvailable = value.characteristics_available === true;
  const area = characteristicsAvailable ? asFiniteNumber(value.area) : null;
  const usableArea = area !== null && area > 0 ? area : null;
  const diagnosis = asDiagnosis(value.diagnosis);

  return {
    kenlo_property_id: asNullableString(value.kenlo_property_id) ?? "",
    property_id: asFiniteNumber(value.property_id),
    listing_property_id: asFiniteNumber(value.listing_property_id),
    contract_code: asNullableString(value.contract_code),
    title: asNullableString(value.title),
    rent_amount: asFiniteNumber(value.rent_amount),
    condominium_name: asNullableString(value.condominium_name),
    condominium_fee: characteristicsAvailable ? asFiniteNumber(value.condominium_fee) : null,
    owner_name: asNullableString(value.owner_name),
    unit: asNullableString(value.unit),
    area: usableArea,
    area_display:
      usableArea === null
        ? "—"
        : asNullableString(value.area_display) ?? `${usableArea} m²`,
    furnished: characteristicsAvailable ? asNullableString(value.furnished) : null,
    situation,
    situation_label:
      asNullableString(value.situation_label) ?? DEFAULT_SITUATION_LABELS[situation],
    contract_start: asNullableString(value.contract_start),
    contract_end: asNullableString(value.contract_end),
    address: asNullableString(value.address),
    city: asNullableString(value.city),
    characteristics_available: characteristicsAvailable,
    characteristics_notice: asNullableString(value.characteristics_notice),
    days_vacant:
      situation === "ALUGANDO" ? asFiniteNumber(value.days_vacant) : null,
    funnel: normalizeFunnel(value.funnel, metricsDays),
    diagnosis: situation === "ALUGANDO" ? diagnosis : null,
    diagnosis_label:
      situation === "ALUGANDO" && diagnosis
        ? asNullableString(value.diagnosis_label)
        : null,
  };
}

function normalizeKpis(value: unknown): PortfolioKpis {
  if (!isRecord(value)) {
    return EMPTY_KPIS;
  }

  return {
    total: asNonNegativeInteger(value.total, 0),
    rented: asNonNegativeInteger(value.rented, 0),
    vacant: asNonNegativeInteger(value.vacant, 0),
    occupancy_rate: asFiniteNumber(value.occupancy_rate),
    vacant_monthly_rent: asFiniteNumber(value.vacant_monthly_rent),
  };
}

function normalizeAppliedFilters(value: unknown): PortfolioAppliedFilters {
  const filters = isRecord(value) ? value : {};
  const situation = asNullableString(filters.situation);

  return {
    situation:
      situation && (PORTFOLIO_SITUATIONS as readonly string[]).includes(situation)
        ? (situation as PortfolioSituation)
        : null,
    condominium: asNullableString(filters.condominium),
    sort: asSort(filters.sort),
    metrics_days: asMetricsDays(filters.metrics_days),
  };
}

function normalizeSituationLabels(
  value: unknown
): Record<PortfolioSituation, string> {
  const source = isRecord(value) ? value : {};

  return PORTFOLIO_SITUATIONS.reduce((labels, situation) => {
    labels[situation] =
      asNullableString(source[situation]) ?? DEFAULT_SITUATION_LABELS[situation];
    return labels;
  }, { ...DEFAULT_SITUATION_LABELS });
}

export function normalizeOwnerPortfolio(payload: unknown): OwnerPortfolioData {
  const data = isRecord(payload) ? payload : {};
  const appliedFilters = normalizeAppliedFilters(data.applied_filters);
  const properties = Array.isArray(data.properties)
    ? data.properties
        .map((item) => normalizeProperty(item, appliedFilters.metrics_days))
        .filter((item): item is PortfolioProperty => item !== null)
    : [];

  return {
    kpis: normalizeKpis(data.kpis),
    condominiums: Array.isArray(data.condominiums)
      ? data.condominiums
          .map((name) => asNullableString(name))
          .filter((name): name is string => name !== null)
      : [],
    properties,
    property_ids: Array.isArray(data.property_ids)
      ? data.property_ids
          .map((id) => asFiniteNumber(id))
          .filter((id): id is number => id !== null)
      : [],
    applied_filters: appliedFilters,
    situation_labels: normalizeSituationLabels(data.situation_labels),
  };
}
